'use client';

import { c as _c } from "react/compiler-runtime";
import type { Vehicle, Vendor } from '@cruz-com/lookup';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import Link from 'next/link';
import { useMemo } from 'react';
import { BsChatSquareText } from 'react-icons/bs';
import { HiOutlineSquares2X2 } from 'react-icons/hi2';
import { SiGooglemaps } from 'react-icons/si';
import { VscCallOutgoing } from 'react-icons/vsc';
import { twMerge } from 'tailwind-merge';
import { vendorApi } from '@/api';
import { RatingStars } from '@/components';
import { LeadTypeEnum, lkpLeadList, trackingLocationPhoneClick } from '@/lib';
import { useGlobalStore } from '@/store/provider';
type Props = {
  id?: string;
  vehicle?: Vehicle.Detail;
  initialData?: Vendor.Location | undefined;
  showMessageDealer: boolean;
  showLinkToInventory: boolean;
  showRequestHistoryReport?: boolean;
};
const btnStyle = 'flex items-center justify-center gap-x-3 gap-y-1.5 p-4 text-sm font-normal truncate w-full flex-col md:flex-row';
export const DealerLocationTop = t0 => {
  const $ = _c(56);
  const {
    id,
    vehicle,
    initialData,
    showMessageDealer,
    showLinkToInventory,
    showRequestHistoryReport
  } = t0;
  const {
    setLead,
    scrollTopOver
  } = useGlobalStore(_temp);
  const t1 = `Location_${id}`;
  let t2;
  if ($[0] !== id || $[1] !== t1) {
    t2 = [t1, id];
    $[0] = id;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== id) {
    t3 = () => vendorApi.getLocation(id as string);
    $[3] = id;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== initialData || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: 1000,
      initialData,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false
    };
    $[5] = initialData;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data
  } = useQuery(t4);
  let t5;
  if ($[9] !== data) {
    t5 = data || {};
    $[9] = data;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    name,
    address1,
    city,
    state_abbr,
    zip_code,
    phone,
    distance,
    place,
    dealer_url
  } = t5;
  place?.google_map_uri;
  let x = 4;
  if (!showMessageDealer) {
    x = 3;
  }
  if (!place?.google_map_uri) {
    x = x - 1;
  }
  if (!showLinkToInventory) {
    x = x - 1;
  }
  const gridCols = `grid-cols-${x}`;
  let t6;
  if ($[11] !== showRequestHistoryReport) {
    t6 = showRequestHistoryReport ? lkpLeadList : _.filter(lkpLeadList, _temp2);
    $[11] = showRequestHistoryReport;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const leadTypeList = t6;
  if (_.isEmpty(data)) {
    return null;
  }
  const renderRatings = _temp3;
  let t7;
  if ($[13] !== leadTypeList || $[14] !== setLead || $[15] !== vehicle?._id || $[16] !== vehicle?.title) {
    t7 = () => <Popover as="div" className="relative"><PopoverButton className={twMerge(btnStyle, "focus-visible:outline-none")} aria-label="Contact Options"><BsChatSquareText className="size-5" aria-hidden="true" /><span>Contact</span></PopoverButton><PopoverPanel as="nav" aria-label="Contact Seller Options" anchor="bottom start" transition={true} modal={false} className="menu-items-rev w-[300px]">{_.map(leadTypeList, t8 => {
          const {
            id: id_0,
            name: name_0
          } = t8;
          return <button key={`dml-${id_0}`} type="button" onClick={() => {
            setLead({
              type: id_0,
              vehicle_id: vehicle?._id as string || "",
              vehicle_title: vehicle?.title || ""
            });
          }} className="menu-item-rev h-14">{name_0}</button>;
        })}</PopoverPanel></Popover>;
    $[13] = leadTypeList;
    $[14] = setLead;
    $[15] = vehicle?._id;
    $[16] = vehicle?.title;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const renderMessageDealerMenu = t7;
  let t8;
  if ($[18] !== name) {
    t8 = <h2 id="dealer-mobile-heading" className="py-0! text-xl font-bold">{name}</h2>;
    $[18] = name;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = `${address1}, ${city}, ${state_abbr} ${zip_code}`;
  let t10;
  if ($[20] !== distance) {
    t10 = distance && ` (${distance.toLocaleString()} mi. away)`;
    $[20] = distance;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t9) {
    t11 = <address className="mt-1 text-sm/6 font-light not-italic" aria-label="Dealer Address">{t9}{t10}</address>;
    $[22] = t10;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = place?.rating || 0;
  let t13;
  if ($[25] !== t12) {
    t13 = renderRatings(t12);
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t11 || $[28] !== t13 || $[29] !== t8) {
    t14 = <div className="mb-3 px-4 lg:hidden">{t8}{t11}{t13}</div>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t8;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = scrollTopOver && "shadow-xl";
  let t16;
  if ($[31] !== gridCols || $[32] !== t15) {
    t16 = twMerge("sticky top-0 right-0 left-0 z-10 mb-8 lg:hidden", "grid divide-x divide-zinc-500 border-t border-b", "bg-zinc-800 text-white dark:bg-zinc-300 dark:text-black", gridCols, t15);
    $[31] = gridCols;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== renderMessageDealerMenu || $[35] !== showMessageDealer) {
    t17 = showMessageDealer && renderMessageDealerMenu();
    $[34] = renderMessageDealerMenu;
    $[35] = showMessageDealer;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== id || $[38] !== phone || $[39] !== showLinkToInventory) {
    t18 = phone && <a href={`tel:${phone}`} onClick={() => trackingLocationPhoneClick(id || "")} rel="noreferrer" className={twMerge(btnStyle, showLinkToInventory && "flex-col md:flex-row")} aria-label={`Call dealer at ${phone}`}><VscCallOutgoing className="size-5" aria-hidden="true" /><span>Call</span></a>;
    $[37] = id;
    $[38] = phone;
    $[39] = showLinkToInventory;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== place || $[42] !== showLinkToInventory) {
    t19 = place?.google_map_uri && <a href={place.google_map_uri} className={twMerge(btnStyle, showLinkToInventory && "flex-col md:flex-row")} target="_blank" rel="noreferrer" aria-label="Open dealer location in Google Maps"><SiGooglemaps className="size-5" aria-hidden="true" /><span>Maps</span></a>;
    $[41] = place;
    $[42] = showLinkToInventory;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== dealer_url || $[45] !== showLinkToInventory) {
    t20 = showLinkToInventory && <Link href={dealer_url || ""} className={twMerge(btnStyle, "flex-col md:flex-row")} hrefLang="en" aria-label="View dealer inventory"><HiOutlineSquares2X2 className="size-5" aria-hidden="true" /><span>Inventory</span></Link>;
    $[44] = dealer_url;
    $[45] = showLinkToInventory;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20) {
    t21 = <nav aria-label="Dealer Quick Actions" className={t16}>{t17}{t18}{t19}{t20}</nav>;
    $[47] = t16;
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t14 || $[54] !== t21) {
    t22 = <section aria-labelledby="dealer-mobile-heading">{t14}{t21}</section>;
    $[53] = t14;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  return t22;
};
function _temp(state) {
  return state;
}
function _temp2(x_0) {
  return x_0.id !== LeadTypeEnum.RequestHistoryReport;
}
function _temp3(rating) {
  if (!rating || rating < 3) {
    return null;
  }
  return <div className="mt-1 flex items-center gap-2"><div className="text-sm">{rating}</div><RatingStars rating={rating} size={5} /></div>;
}